import ApiService from 'api-client/ApiService'
import config from 'app-config'

export interface PrometheusResponse<T = any> {
  status: string
  isPartial?: boolean
  data: {
    resultType: string
    result: T[]
  }
  stats?: {
    seriesFetched: string
    executionTimeMsec: number
  }
}

// FR-001: Prometheus client extends ApiService for automatic X-Auth-Token inclusion
class Prometheus extends ApiService {
  public getClassName() {
    return 'prometheus'
  }

  protected async getEndpoint() {
    return Promise.resolve(config.apiHost)
  }

  get baseUrl(): string {
    return '/select/0/prometheus/api/v1'
  }

  // FR-002: queryRange for time-series queries
  queryRange = async <T = any>(
    query: string,
    start: number,
    end: number,
    step: string | number,
  ): Promise<PrometheusResponse<T>> => {
    const url = `${this.baseUrl}/query_range?query=${query}&start=${start}&end=${end}&step=${step}`
    // FR-004: extractNestedData false to preserve Prometheus response structure
    // normalizeResponse with extractNestedData:false returns response.data which is
    // the full Prometheus JSON. The conditional return type of normalizeResponse
    // incorrectly narrows this, so we cast.
    return (this.client.basicGet({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'queryRange',
        extractNestedData: false,
      },
    }) as unknown) as Promise<PrometheusResponse<T>>
  }

  // Authenticated GET for a pre-built Prometheus URL path (used by useTrendData
  // which constructs URLs from METRICS_ENDPOINTS config)
  fetchUrl = async <T = any>(url: string): Promise<PrometheusResponse<T>> => {
    return (this.client.basicGet({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'fetchUrl',
        extractNestedData: false,
      },
    }) as unknown) as Promise<PrometheusResponse<T>>
  }

  // FR-003: query for instant queries
  query = async <T = any>(
    query: string,
    time?: number,
    step?: string | number,
  ): Promise<PrometheusResponse<T>> => {
    let url = `${this.baseUrl}/query?query=${query}`
    if (time !== undefined) url += `&time=${time}`
    if (step !== undefined) url += `&step=${step}`
    // FR-004: extractNestedData false to preserve Prometheus response structure
    // See queryRange comment for cast rationale
    return (this.client.basicGet({
      url,
      options: {
        clsName: this.getClassName(),
        mthdName: 'query',
        extractNestedData: false,
      },
    }) as unknown) as Promise<PrometheusResponse<T>>
  }
}

export default Prometheus
